import { useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  CheckIcon,
  CopyIcon,
  ListBulletsIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MultiValueInput } from '@/components/common/MultiValueInput';
import {
  ITEM_NUMBER_PILL_LIMIT,
  ITEM_NUMBER_SPLIT_PATTERN,
  joinItemNumbers,
  splitItemNumbers,
  type ItemFilterResolution,
} from './catalogItemNumbers';

const COPY_FEEDBACK_MS = 1200;

// "Copied" feedback state for the copy buttons below. Clipboard access is
// best-effort: unavailable on non-secure contexts, where the button simply
// shows no check.
function useCopyValues(values: readonly string[]) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(values.join('\n'));
    } catch {
      return;
    }
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  };

  return { copied, copy };
}

export function CopyValuesButton({
  values,
  label,
}: {
  values: readonly string[];
  label: string;
}) {
  const { copied, copy } = useCopyValues(values);
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5 text-xs"
      onClick={() => void copy()}
    >
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5" />
      ) : (
        <CopyIcon className="h-3.5 w-3.5" />
      )}
      {label}
    </Button>
  );
}

/**
 * The "Multiple items" mode switch — rendered inside the search box beside the
 * regex toggle, and kept in the same spot on the pill input so the way back is
 * where the way in was.
 */
export function MultiItemsToggle({
  active,
  onToggle,
}: {
  active: boolean;
  onToggle: () => void;
}) {
  const gt = useGT();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={active}
          aria-label={gt('Search multiple items', {
            context:
              'toggle that swaps the catalog search box for a paste-many-item-numbers input',
          })}
          className={`rounded px-1 py-0.5 leading-none transition-colors${active ? ' bg-primary/15 text-primary' : ' text-muted-foreground hover:bg-muted'}`}
        >
          <ListBulletsIcon className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <T context="tooltip on the catalog multiple-items search toggle">
          Search multiple items
        </T>
      </TooltipContent>
    </Tooltip>
  );
}

// The scrollable value list inside the summary chip's popover — the management
// surface once the set is too large for pills.
function ValueListPopoverContent({
  values,
  onRemoveValue,
  onClear,
}: {
  values: string[];
  onRemoveValue: (index: number) => void;
  onClear: () => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <CopyValuesButton
          values={values}
          label={gt('Copy all', {
            context: 'button copying every value of the multiple-items filter',
          })}
        />
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={onClear}
        >
          <XIcon className="h-3.5 w-3.5" />
          <T context="button clearing every value of the multiple-items filter">
            Clear all
          </T>
        </Button>
      </div>
      <ul className="max-h-64 overflow-y-auto rounded-md border divide-y">
        {values.map((value, index) => (
          <li
            key={`${value}-${index}`}
            className="flex items-center justify-between gap-2 px-2 py-1 text-xs font-mono"
          >
            <span className="truncate">{value}</span>
            <button
              type="button"
              onClick={() => onRemoveValue(index)}
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
              aria-label={gt('Remove {value}', {
                value,
                context: 'aria label to remove one multiple-items filter value',
              })}
            >
              <XIcon className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Above ITEM_NUMBER_PILL_LIMIT values the pills collapse into this one chip;
// its popover holds the full (scrollable) list with copy/clear/remove.
function CollapsedValuesChip({
  values,
  notFoundCount,
  onRemoveValue,
  onClear,
}: {
  values: string[];
  notFoundCount: number;
  onRemoveValue: (index: number) => void;
  onClear: () => void;
}) {
  const gt = useGT();
  const summary =
    notFoundCount > 0
      ? gt('{count} items · {notFound} not found', {
          context:
            'summary chip when the multiple-items filter holds too many values to show as pills',
          count: values.length.toLocaleString(),
          notFound: notFoundCount.toLocaleString(),
        })
      : gt('{count} items', {
          context:
            'summary chip when the multiple-items filter holds too many values to show as pills',
          count: values.length.toLocaleString(),
        });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge
          role="button"
          tabIndex={0}
          variant="secondary"
          className="cursor-pointer gap-1 py-0 font-normal"
        >
          {summary}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <ValueListPopoverContent
          values={values}
          onRemoveValue={onRemoveValue}
          onClear={onClear}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The multiple-items pill input the search box swaps to: paste item numbers
 * (whitespace/comma/semicolon separated) and the catalog shows exactly those
 * products. Individual pills up to {@link ITEM_NUMBER_PILL_LIMIT} values;
 * above that, one summary chip whose popover manages the set.
 */
export function CatalogItemNumbersInput({
  values,
  onValuesChange,
  notFoundCount,
  invalid = false,
  invalidMessage,
  toggle,
  className,
}: {
  values: string[];
  onValuesChange: (values: string[]) => void;
  /** Resolved-but-unmatched count, folded into the collapsed summary chip. */
  notFoundCount: number;
  invalid?: boolean;
  invalidMessage?: string;
  /** The catalog's mode switch. Omitted where the input stands alone (the
   * Lists page's new-list dialog) — no toggle renders then. */
  toggle?: { active: boolean; onToggle: () => void };
  className?: string;
}) {
  const gt = useGT();
  const collapsed = values.length > ITEM_NUMBER_PILL_LIMIT;

  return (
    <div
      className={`relative w-full ${className ?? 'max-w-sm'}`}
      title={invalid ? invalidMessage : undefined}
    >
      <MultiValueInput
        value={joinItemNumbers(values)}
        onChange={(next) => onValuesChange(splitItemNumbers(next))}
        splitPattern={ITEM_NUMBER_SPLIT_PATTERN}
        placeholder={gt('Paste item numbers', {
          context: 'placeholder of the catalog multiple-items pill input',
        })}
        className={`${toggle ? 'pr-9' : ''}${invalid ? ' border-destructive focus-within:ring-destructive' : ''}`}
        {...(collapsed && {
          renderValues: (pills: string[], removePill: (i: number) => void) => (
            <CollapsedValuesChip
              values={pills}
              notFoundCount={notFoundCount}
              onRemoveValue={removePill}
              onClear={() => onValuesChange([])}
            />
          ),
        })}
      />
      {toggle && (
        <span className="absolute right-1.5 top-[7px]">
          <MultiItemsToggle active={toggle.active} onToggle={toggle.onToggle} />
        </span>
      )}
    </div>
  );
}

/**
 * The resolution outcome beside the pill input: how many pasted values matched
 * nothing (with the list and a copy button behind a popover, so they can be
 * chased in the source spreadsheet), and how many matched more than one
 * product (duplicate SKUs across divisions — every match is shown).
 */
export function ItemFilterResolutionBadges({
  resolution,
}: {
  resolution: ItemFilterResolution | undefined;
}) {
  const gt = useGT();
  if (!resolution) return null;
  const { unmatchedInputs, multiMatchInputCount } = resolution;
  return (
    <>
      {unmatchedInputs.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Badge
              role="button"
              tabIndex={0}
              variant="destructive"
              className="cursor-pointer font-normal"
            >
              {gt('{count} not found', {
                context:
                  'badge showing how many pasted item numbers matched no product',
                count: unmatchedInputs.length.toLocaleString(),
              })}
            </Badge>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3" align="start">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium">
                  <T context="heading of the list of pasted item numbers that matched no product">
                    Not found in the catalog
                  </T>
                </span>
                <CopyValuesButton
                  values={unmatchedInputs}
                  label={gt('Copy', {
                    context: 'button copying the unmatched item numbers',
                  })}
                />
              </div>
              <ul className="max-h-64 overflow-y-auto rounded-md border divide-y">
                {unmatchedInputs.map((value) => (
                  <li key={value} className="px-2 py-1 text-xs font-mono">
                    {value}
                  </li>
                ))}
              </ul>
            </div>
          </PopoverContent>
        </Popover>
      )}
      {multiMatchInputCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="font-normal">
              {gt('{count} matched multiple products', {
                context:
                  'badge showing how many pasted item numbers matched more than one product',
                count: multiMatchInputCount.toLocaleString(),
              })}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <T context="tooltip explaining the matched-multiple-products badge">
              An item number can be shared by several products — every match is
              included in the results.
            </T>
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );
}
